import { Bell, CheckCheck, RefreshCcw, Search, Trash2 } from 'lucide-react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { notificationService, type NotificationRecord } from '../../../shared/services';
import { useShop } from '../../../shared/store';

type NotificationsFilter = 'all' | 'unread';

const formatDate = (value?: string): string => {
  if (!value) return '';
  try {
    return new Intl.DateTimeFormat('en-US', {
      month: 'short',
      day: 'numeric',
      year: '2-digit',
      hour: 'numeric',
      minute: '2-digit',
    }).format(new Date(value));
  } catch {
    return value;
  }
};

const normalizePreview = (value: string): string => value.replace(/\s+/g, ' ').trim();

export const BuyerNotificationsPanel = () => {
  const { t } = useTranslation();
  const { currentUser } = useShop();

  const [notifications, setNotifications] = useState<NotificationRecord[]>([]);
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [filter, setFilter] = useState<NotificationsFilter>('all');
  const [searchValue, setSearchValue] = useState('');
  const [actionFeedback, setActionFeedback] = useState('');
  const [actionLoading, setActionLoading] = useState(false);

  const refresh = useCallback(async () => {
    if (!currentUser) {
      setNotifications([]);
      return;
    }

    try {
      setLoading(true);
      setError('');
      setActionFeedback('');
      const list = await notificationService.getMine();
      const sorted = [...list].sort((a, b) => {
        const aTime = a.createdAt ? new Date(a.createdAt).getTime() : 0;
        const bTime = b.createdAt ? new Date(b.createdAt).getTime() : 0;
        return bTime - aTime;
      });
      setNotifications(sorted);
      if (sorted.length > 0 && (selectedId == null || !sorted.some((n) => n.id === selectedId))) {
        setSelectedId(sorted[0].id);
      }
    } catch (err: any) {
      setError(err?.message || 'Failed to load notifications.');
    } finally {
      setLoading(false);
    }
  }, [currentUser, selectedId]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const unreadCount = useMemo(
    () => notifications.filter((notification) => notification.isOpenned === false).length,
    [notifications],
  );

  const filteredNotifications = useMemo(() => {
    const search = searchValue.trim().toLowerCase();
    return notifications.filter((notification) => {
      if (filter === 'unread' && notification.isOpenned !== false) return false;
      if (!search) return true;
      return normalizePreview(notification.notificationBody).toLowerCase().includes(search);
    });
  }, [filter, notifications, searchValue]);

  const selectedNotification = useMemo(
    () => notifications.find((notification) => notification.id === selectedId) ?? null,
    [notifications, selectedId],
  );

  const openNotification = useCallback(
    async (notificationId: number) => {
      if (!notificationId) return;

      setSelectedId(notificationId);
      const candidate = notifications.find((n) => n.id === notificationId);
      if (candidate?.isOpenned === true) {
        return;
      }

      try {
        setActionFeedback('');
        await notificationService.getOne(notificationId);
        setNotifications((prev) =>
          prev.map((n) => (n.id === notificationId ? { ...n, isOpenned: true } : n)),
        );
      } catch (err: any) {
        setActionFeedback(err?.message || 'Unable to open notification.');
      }
    },
    [notifications],
  );

  const deleteNotification = useCallback(
    async (notificationId: number) => {
      if (!notificationId) return;
      try {
        setActionLoading(true);
        setActionFeedback('');
        await notificationService.delete(notificationId);
        setNotifications((prev) => prev.filter((n) => n.id !== notificationId));
        setSelectedId((prevSelected) => {
          if (prevSelected !== notificationId) return prevSelected;
          const remaining = notifications.filter((n) => n.id !== notificationId);
          return remaining.length > 0 ? remaining[0].id : null;
        });
      } catch (err: any) {
        setActionFeedback(err?.message || 'Unable to delete notification.');
      } finally {
        setActionLoading(false);
      }
    },
    [notifications],
  );

  const markAllRead = useCallback(async () => {
    const unread = notifications.filter((notification) => notification.isOpenned === false);
    if (unread.length === 0) return;

    try {
      setActionLoading(true);
      setActionFeedback('');
      for (const notification of unread) {
        await notificationService.getOne(notification.id);
      }
      setNotifications((prev) => prev.map((n) => ({ ...n, isOpenned: true })));
      setActionFeedback('Marked all as read.');
    } catch (err: any) {
      setActionFeedback(err?.message || 'Unable to mark notifications as read.');
    } finally {
      setActionLoading(false);
    }
  }, [notifications]);

  if (!currentUser) {
    return (
      <div className="rounded-2xl border border-slate-200 bg-white p-8 text-center text-slate-700 shadow-sm">
        <Bell className="mx-auto h-10 w-10 text-slate-400" />
        <p className="mt-3 font-medium">{t('dashboardBuyer.notifications.signInTitle', 'Sign in to see notifications')}</p>
        <p className="mt-1 text-sm text-slate-500">
          {t('dashboardBuyer.notifications.signInDescription', 'Notifications appear here after an admin sends them to you.')}
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-3 rounded-3xl bg-gradient-to-r from-orange-500 to-orange-600 p-6 text-white shadow-lg sm:flex-row sm:items-center sm:justify-between sm:p-8">
        <div>
          <h2 className="text-2xl font-bold">{t('dashboardBuyer.sections.notifications.title', 'Notifications')}</h2>
          <p className="mt-1 text-orange-100">
            {t('dashboardBuyer.sections.notifications.description', 'Messages sent to you by the admin.')}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <button
            type="button"
            onClick={refresh}
            disabled={loading || actionLoading}
            className="inline-flex items-center gap-2 rounded-full bg-white/15 px-4 py-2 text-sm font-semibold text-white ring-1 ring-white/20 transition hover:bg-white/20 disabled:cursor-not-allowed disabled:opacity-60"
          >
            <RefreshCcw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
            {t('dashboardBuyer.notifications.refresh', 'Refresh')}
          </button>
          <button
            type="button"
            onClick={markAllRead}
            disabled={actionLoading || unreadCount === 0}
            className="inline-flex items-center gap-2 rounded-full bg-white px-4 py-2 text-sm font-semibold text-orange-700 shadow-sm transition hover:bg-orange-50 disabled:cursor-not-allowed disabled:opacity-70"
          >
            <CheckCheck className="h-4 w-4" />
            {t('dashboardBuyer.notifications.markAllRead', 'Mark all read')}
          </button>
        </div>
      </div>

      {error ? (
        <div className="rounded-2xl border border-rose-200 bg-rose-50 p-6 text-sm text-rose-700">{error}</div>
      ) : null}
      {actionFeedback ? (
        <div className="rounded-2xl border border-slate-200 bg-white p-4 text-sm text-slate-700 shadow-sm">{actionFeedback}</div>
      ) : null}

      <div className="grid gap-6 lg:grid-cols-3">
        <div className="rounded-3xl border border-slate-200 bg-white shadow-sm">
          <div className="border-b border-slate-100 p-4">
            <div className="flex items-center justify-between gap-3">
              <p className="text-sm font-semibold text-slate-900">
                {t('dashboardBuyer.notifications.inbox', 'Inbox')}
                {unreadCount > 0 ? (
                  <span className="ml-2 inline-flex items-center rounded-full bg-orange-100 px-2 py-0.5 text-xs font-semibold text-orange-700">
                    {unreadCount} {t('dashboardBuyer.notifications.unread', 'unread')}
                  </span>
                ) : null}
              </p>
              <div className="flex items-center gap-1 rounded-full bg-slate-100 p-1">
                <button
                  type="button"
                  onClick={() => setFilter('all')}
                  className={`rounded-full px-3 py-1 text-xs font-semibold transition ${
                    filter === 'all' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-600 hover:text-slate-900'
                  }`}
                >
                  {t('dashboardBuyer.notifications.filterAll', 'All')}
                </button>
                <button
                  type="button"
                  onClick={() => setFilter('unread')}
                  className={`rounded-full px-3 py-1 text-xs font-semibold transition ${
                    filter === 'unread' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-600 hover:text-slate-900'
                  }`}
                >
                  {t('dashboardBuyer.notifications.filterUnread', 'Unread')}
                </button>
              </div>
            </div>

            <div className="relative mt-3">
              <Search className="pointer-events-none absolute left-3 top-2.5 h-4 w-4 text-slate-400" />
              <input
                type="text"
                value={searchValue}
                onChange={(e) => setSearchValue(e.target.value)}
                placeholder={t('dashboardBuyer.notifications.searchPlaceholder', 'Search notifications...')}
                className="w-full rounded-xl border border-slate-200 bg-white py-2 pl-10 pr-3 text-sm text-slate-900 focus:border-orange-400 focus:outline-none"
              />
            </div>
          </div>

          <div className="max-h-[520px] overflow-auto p-2">
            {loading && notifications.length === 0 ? (
              <div className="flex items-center justify-center p-10">
                <div className="h-8 w-8 animate-spin rounded-full border-4 border-slate-200 border-t-orange-500" />
              </div>
            ) : filteredNotifications.length === 0 ? (
              <div className="p-10 text-center text-sm text-slate-600">
                <Bell className="mx-auto h-10 w-10 text-slate-300" />
                <p className="mt-3">{t('dashboardBuyer.notifications.empty', 'No notifications found.')}</p>
              </div>
            ) : (
              <div className="space-y-2">
                {filteredNotifications.map((notification) => {
                  const isSelected = notification.id === selectedId;
                  const isUnread = notification.isOpenned === false;
                  return (
                    <button
                      key={notification.id}
                      type="button"
                      onClick={() => openNotification(notification.id)}
                      className={`group w-full rounded-2xl border px-4 py-3 text-left transition ${
                        isSelected
                          ? 'border-orange-200 bg-orange-50'
                          : 'border-slate-100 bg-white hover:border-slate-200 hover:bg-slate-50'
                      }`}
                    >
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0">
                          <div className="flex items-center gap-2">
                            {isUnread ? <span className="h-2 w-2 rounded-full bg-orange-500" /> : null}
                            <p className="truncate text-sm font-semibold text-slate-900">
                              {normalizePreview(notification.notificationBody) || t('dashboardBuyer.notifications.message', 'Message')}
                            </p>
                          </div>
                          <p className="mt-1 truncate text-xs text-slate-500">{formatDate(notification.createdAt)}</p>
                        </div>
                        <span className="opacity-0 transition group-hover:opacity-100">
                          <button
                            type="button"
                            onClick={(event) => {
                              event.preventDefault();
                              event.stopPropagation();
                              deleteNotification(notification.id);
                            }}
                            disabled={actionLoading}
                            className="inline-flex items-center justify-center rounded-full p-2 text-slate-500 transition hover:bg-white hover:text-rose-600 disabled:cursor-not-allowed disabled:opacity-60"
                            aria-label={t('dashboardBuyer.notifications.delete', 'Delete')}
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </span>
                      </div>
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        <div className="lg:col-span-2 rounded-3xl border border-slate-200 bg-white shadow-sm">
          {!selectedNotification ? (
            <div className="flex h-full flex-col items-center justify-center p-12 text-center">
              <Bell className="h-12 w-12 text-slate-300" />
              <p className="mt-4 text-sm font-semibold text-slate-900">{t('dashboardBuyer.notifications.select', 'Select a notification')}</p>
              <p className="mt-1 text-sm text-slate-500">
                {t('dashboardBuyer.notifications.selectDescription', 'Choose a notification on the left to read it here.')}
              </p>
            </div>
          ) : (
            <div className="flex h-full flex-col">
              <div className="flex flex-wrap items-center justify-between gap-3 border-b border-slate-100 p-5">
                <div className="min-w-0">
                  <p className="truncate text-sm font-semibold text-slate-900">
                    {t('dashboardBuyer.notifications.from', 'From')}: {t('dashboardBuyer.notifications.admin', 'Admin')}
                  </p>
                  <p className="mt-1 text-xs text-slate-500">{formatDate(selectedNotification.createdAt)}</p>
                </div>
                <button
                  type="button"
                  onClick={() => deleteNotification(selectedNotification.id)}
                  disabled={actionLoading}
                  className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-700 shadow-sm transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-60"
                >
                  <Trash2 className="h-4 w-4 text-rose-600" />
                  {t('dashboardBuyer.notifications.delete', 'Delete')}
                </button>
              </div>

              <div className="flex-1 p-6">
                <div className="max-w-2xl rounded-3xl bg-orange-50 p-6 text-sm leading-relaxed text-slate-800">
                  {selectedNotification.notificationBody}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default BuyerNotificationsPanel;

